import { apiRequest } from "./api.client";

export interface AvailabilitySchedule {
  schedule_id: string;
  doctor_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  // Per-doctor cap on bookings for this weekday. null → inherit the clinic-wide
  // default (Settings → Appointment Configuration → Max Daily Bookings).
  max_daily_bookings: number | null;
  is_active: boolean;
}

export interface DoctorListItem {
  doctor_id: string;
  /** Cognito sub of the linked user (null if unlinked) — used to match the
   *  signed-in doctor to their own record. */
  user_id?: string | null;
  display_id?: string;
  name: string;
  specialization: string;
  specializations?: string[];   // TEXT[] — present after Alembic migration ships
  department: string;
  registration_number: string | null;
  qualifications: string | null;
  experience_years: number | null;
  consultation_fee: number | null;
  room_location: string | null;
  status: string;
  is_active: boolean;
  availability_schedules: AvailabilitySchedule[];
  total_patients: number;
  today_upcoming: number;
  today_cancelled: number;
}

export interface DoctorFull extends DoctorListItem {
  clinic_id: string;
  user_id: string | null;
  phone: string | null;
  email: string | null;
  /** True when the doctor has a login account: the email is their verified
   *  sign-in identity and cannot be edited. */
  email_locked?: boolean;
  created_at: string;
  updated_at: string;
}

/** Shape returned by GET /api/v1/doctors/roster — used by RosterPage */
export interface DoctorRosterItem {
  doctor_id: string;
  display_id?: string;
  name: string;
  specialization: string;
  department: string;
  status: string;
  is_active: boolean;
  qualifications: string | null;
  experience_years: number | null;
  consultation_fee: number | null;
  room_location: string | null;
  phone: string | null;
  email: string | null;
  availability_schedules: AvailabilitySchedule[];
}

export interface DoctorCreatePayload {
  name: string;
  specialization: string;    // old column — kept for backward compat until Sprint 9 drops it
  specializations?: string[]; // new TEXT[] column — backend ignores until migration ships
  department: string;
  registration_number?: string;
  qualifications?: string;
  experience_years?: number;
  consultation_fee?: number;
  room_location?: string;
  phone?: string;
  email?: string;
  status?: string;
  availability_schedules?: {
    day_of_week: number;
    start_time: string;
    end_time: string;
    slot_duration_minutes: number;
    max_daily_bookings?: number | null;
  }[];
}

export interface DoctorUpdatePayload extends Partial<DoctorCreatePayload> {
  is_active?: boolean;
}

export interface DoctorInviteStatus {
  doctor_id: string;
  has_accepted: boolean;
  cognito_status: string;
}

export interface BlockType {
  block_type_id: string;
  clinic_id: string | null;
  key: string;
  label: string;
  is_active: boolean;
}

/** Deliberately narrower than full iCal RRULE - freq/weekday/until-or-count
 *  only, matching the backend's RecurrenceRule schema. */
export interface RecurrenceRule {
  freq: "daily" | "weekly";
  /** Weekly only; must include the weekday the block itself starts on. */
  byweekday?: ("MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU")[];
  /** "YYYY-MM-DD" - the last clinic-local day an occurrence may start on,
   *  inclusive. A plain date, not an ISO instant. */
  until?: string;
  count?: number;
}

/** occurrence = just this block; following = this occurrence and every later
 *  one in its series (also stops the series being extended). */
export type BlockCancelScope = "occurrence" | "following";

export interface DoctorBlockTime {
  block_id: string;
  doctor_id: string;
  clinic_id: string;
  start_time: string;
  end_time: string;
  reason: string | null;
  block_type_id: string | null;
  all_day: boolean;
  is_vacation: boolean;
  patient_facing_note: string | null;
  recurrence_rule: string | null;
  recurrence_parent_id: string | null;
  recurrence_until: string | null;
  status: "active" | "cancelled";
  created_by_user_id: string | null;
  created_at: string;
  cancelled_at: string | null;
}

export interface ConflictingAppointment {
  appointment_id: string;
  patient_id: string;
  patient_name: string;
  slot_id: string;
  start_time: string;
  end_time: string;
  status: string;
  /** The block - for a recurring series, the specific occurrence - it falls in. */
  block_id?: string | null;
}

export interface DoctorBlockTimeCreateResponse {
  block: DoctorBlockTime;
  conflicting_appointments: ConflictingAppointment[];
  occurrences_materialized: number;
}

export interface DoctorBlockTimeCreatePayload {
  start_time: string;
  end_time: string;
  reason: string;
  block_type_id?: string;
  all_day?: boolean;
  is_vacation?: boolean;
  patient_facing_note?: string;
  recurrence?: RecurrenceRule;
}

export const DoctorAPI = {
  list: (params?: { search?: string; department?: string; is_active?: boolean }): Promise<DoctorListItem[]> => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.department) qs.set("department", params.department);
    if (params?.is_active !== undefined) qs.set("is_active", String(params.is_active));
    const query = qs.toString() ? `?${qs.toString()}` : "";
    return apiRequest<DoctorListItem[]>(`/doctors${query}`);
  },
  // Alias used by DoctorManagementPage — calls list() with no filters
  getAll: (): Promise<DoctorListItem[]> => {
    return apiRequest<DoctorListItem[]>("/doctors");
  },
  // Alias used by DoctorManagementPage — returns summary stats from /doctors/stats
  getStats: () => {
    return apiRequest<{ totalDoctors: number; presentToday: number; departments: number; patientsToday: number }>("/doctors/stats");
  },
  /** Roster view — returns all active doctors with their availability schedules */
  getRoster: (): Promise<DoctorRosterItem[]> => {
    return apiRequest<DoctorRosterItem[]>("/doctors/roster");
  },
  get: (id: string): Promise<DoctorFull> => apiRequest<DoctorFull>(`/doctors/${id}`),
  create: (data: DoctorCreatePayload): Promise<DoctorFull> =>
    apiRequest<DoctorFull>("/doctors", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: DoctorUpdatePayload): Promise<DoctorFull> =>
    apiRequest<DoctorFull>(`/doctors/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  getDepartments: (): Promise<string[]> => apiRequest<string[]>("/doctors/departments"),
  delete: (id: string): Promise<void> =>
    apiRequest<void>(`/doctors/${id}`, { method: "DELETE" }),
  getInviteStatus: (doctorId: string): Promise<DoctorInviteStatus> =>
    apiRequest<DoctorInviteStatus>(`/doctors/${doctorId}/invite-status`),
  resendInvite: (doctorId: string): Promise<{ message: string }> =>
    apiRequest<{ message: string }>(`/doctors/${doctorId}/resend-invite`, { method: "POST" }),

  createBlockTime: (
    doctorId: string, data: DoctorBlockTimeCreatePayload,
  ): Promise<DoctorBlockTimeCreateResponse> =>
    apiRequest<DoctorBlockTimeCreateResponse>(`/doctors/${doctorId}/block-time`, {
      method: "POST", body: JSON.stringify(data),
    }),

  listBlockTime: (doctorId: string): Promise<DoctorBlockTime[]> =>
    apiRequest<DoctorBlockTime[]>(`/doctors/${doctorId}/block-time`),

  cancelBlockTime: (
    doctorId: string, blockId: string, scope: BlockCancelScope = "occurrence",
  ): Promise<DoctorBlockTime> =>
    apiRequest<DoctorBlockTime>(`/doctors/${doctorId}/block-time/${blockId}/cancel?scope=${scope}`, {
      method: "POST",
    }),

  listBlockTypes: (): Promise<BlockType[]> => apiRequest<BlockType[]>("/block-types"),
};